import { mande, type MandeError } from 'mande'
import type { GeneratedRecoveryCodes } from '~/types/auth/recovery'
import type { LoginResponse } from '~/types/auth/responses'
import type { TOTPSetupResponse } from '~/types/auth/totp'
import type User from '~/types/auth/user'
import type { WebauthnCredential, WebauthnLogin } from '~/types/auth/webauthn'
import { UnauthenticatedException } from '~/types/exceptions'
import type { AuthenticationResponseJSON, PublicKeyCredentialCreationOptionsJSON, RegistrationResponseJSON } from '@simplewebauthn/browser'

export default function useUserApi() {
  const config = useRuntimeConfig()
  const user = mande(config.public.apiUrl + '/auth', {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
  })

  const me = async () => {
    try {
      return await user.get<User>('/me')
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      return await user.post<LoginResponse>('/login', { email, password })
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const logout = async () => {
    try {
      return await user.post('/logout')
    } catch (error) {
      const err = useHttpError(error as MandeError)
      if (!(err instanceof UnauthenticatedException)) {
        throw err
      }
    }
  }

  const generateTotp = async () => {
    try {
      return await user.post<TOTPSetupResponse>('/totp/enroll')
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const confirmTotp = async (id: string, code: string) => {
    try {
      return await user.post('/totp/confirm', {
        auth_id: id,
        code: code,
      })
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const verifyTotp = async (code: string) => {
    try {
      return await user.post('/totp/verify', {
        code: code,
      })
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const generateRecoveryCodes = async () => {
    try {
      return await user.post<GeneratedRecoveryCodes>('/recovery/generate')
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const verifyRecoveryCode = async (code: string) => {
    try {
      return await user.post('/recovery/verify', { code })
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const isAdmin = async () => {
    try {
      await user.get('/admin')
      return true
    } catch {
      return false
    }
  }

  const getWebauthnCredentials = async () => {
    try {
      return await user.get<WebauthnCredential[]>('/webauthn')
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const beginWebauthnRegistration = async () => {
    try {
      return await user.post<{ publicKey: PublicKeyCredentialCreationOptionsJSON }>('/webauthn/register')
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const storeWebauthnCredential = async (body: RegistrationResponseJSON) => {
    try {
      return await user.post<WebauthnCredential>('/webauthn/store', body)
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const deleteWebauthnCredential = async (id: string) => {
    try {
      await user.delete(`/webauthn/${id}`)
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const renameWebauthnCredential = async (id: string, name: string) => {
    try {
      return await user.patch<WebauthnCredential>(`/webauthn/${id}`, { name })
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const beginWebauthnLogin = async () => {
    try {
      return await user.post<WebauthnLogin>('/webauthn/begin')
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  const finishWebauthnLogin = async (id: string, data: AuthenticationResponseJSON) => {
    try {
      return await user.post<LoginResponse>('/webauthn/login', {
        id,
        data,
      })
    } catch (error) {
      throw useHttpError(error as MandeError)
    }
  }

  return {
    me,
    login,
    logout,
    generateTotp,
    confirmTotp,
    verifyTotp,
    generateRecoveryCodes,
    verifyRecoveryCode,
    isAdmin,
    getWebauthnCredentials,
    beginWebauthnRegistration,
    storeWebauthnCredential,
    deleteWebauthnCredential,
    renameWebauthnCredential,
    beginWebauthnLogin,
    finishWebauthnLogin,
  }
}
